import Vue from 'vue'
import levelgraph from 'levelgraph'
import s from 'string'

import ApplicationException from '@/errors/ApplicationException'
import sublevel from '@/services/SubLevelAdapterService'

const promisify = o =>
  Object.getOwnPropertyNames(o).forEach(prop => {
    if (typeof o[prop] === 'function') {
      if (!o[prop + 'Async']) {
        o[prop + 'Async'] = (...args) =>
          new Promise((resolve, reject) => {
            o[prop](...args, (err, result) => {
              if (err) return reject(err)
              return resolve(result)
            })
          })
      }
    }
  })

const module = {
  namespaced: true, // user
  state: () => ({
    space: null,
    my: {
      subjects: [],
      predicates: [],
      objects: []
    },
    recollection: {
      memories: [],
      subject: false,
      predicate: false,
      object: false
    },
    err: false
  }),
  getters: {
    workspace: (state, getters, rootState, rootGetters) => rootGetters.isConnected ? sublevel.sub(rootState.schema, 'memories', { keyEncoding: 'utf8', valueEncoding: 'utf8' }) : null,
    isLoggedIn: (state, getters, rootState, rootGetters) => rootGetters['user/login/isLoggedIn'],
    isLoggedOut: (state, getters, rootState, rootGetters) => rootGetters['user/login/isLoggedOut'],
    isWorkspaceUnavailable: (state, getters, rootState, rootGetters) => (getters.workspace === null),
    isWorkspaceAvailable: (state, getters, rootState, rootGetters) => (getters.workspace !== null),
    isHooked: (state, getters, rootState, rootGetters) => (state.space !== null),
    isUnhooked: (state, getters, rootState, rootGetters) => (state.space === null),
    hasErr: (state, getters, rootState, rootGetters) => (state.err !== false)
  },
  mutations: {
    HOOK (state, space) {
      state.space = space
      state.err = false
    },
    UNHOOK (state) {
      state.space = null

      Vue.set(state.my, 'subjects', [])
      Vue.set(state.my, 'predicates', [])
      Vue.set(state.my, 'objects', [])

      Vue.set(state.recollection, 'memories', [])
      Vue.set(state.recollection, 'subject', false)
      Vue.set(state.recollection, 'predicate', false)
      Vue.set(state.recollection, 'object', false)

      state.err = false
    },
    REFRESH (state, opts) {
      Vue.set(state.my, 'subjects', opts.subjects)
      Vue.set(state.my, 'predicates', opts.predicates)
      Vue.set(state.my, 'objects', opts.objects)
    },
    RECOLLECT (state, opts) {
      Vue.set(state.recollection, 'memories', opts.memories)
      Vue.set(state.recollection, 'subject', opts.subject)
      Vue.set(state.recollection, 'predicate', opts.predicate)
      Vue.set(state.recollection, 'object', opts.object)
    },
    FORGOTTEN (state, opts) {
      state.recollection.memories.splice(opts, 1)
    },
    USER_ERR (state, err) {
      state.err = err
    }
  },
  actions: {
    async hook ({ state, rootState, commit, dispatch, getters, rootGetters }, opts) {
      if (getters.isLoggedOut) return
      if (getters.isHooked) return

      let username = rootState.user.login.user.name

      try {
        if (getters.isWorkspaceUnavailable) throw ApplicationException('Workspace is not available', 'WorkspaceError')
        let space = levelgraph(sublevel.sub(getters.workspace, username, { keyEncoding: 'utf8', valueEncoding: 'json' }))
        promisify(space)
        commit('HOOK', space)
      } catch (e) {
        commit('USER_ERR', e.message)
        throw e
      }
    },
    async unhook ({ state, rootState, commit, dispatch, getters, rootGetters }, opts) {
      if (getters.isLoggedOut) return
      if (getters.isUnhooked) return

      commit('UNHOOK')
    },
    async refresh ({ state, rootState, commit, dispatch, getters, rootGetters }, opts) {
      if (getters.isLoggedOut) return

      let space = state.space

      let subjects = new Set()
      let predicates = new Set()
      let objects = new Set()

      try {
        let memories = await space.searchAsync({ subject: space.v('subject'), predicate: space.v('predicate'), object: space.v('object') })
        memories.forEach(memory => {
          subjects.add(memory.subject)
          predicates.add(memory.predicate)
          objects.add(memory.object)
        })

        commit('REFRESH', { subjects: [...subjects], predicates: [...predicates], objects: [...objects] })
      } catch (e) {
        commit('USER_ERR', e.message)
        throw e
      }
    },
    async remember ({ state, rootState, commit, dispatch, getters, rootGetters }, opts) {
      if (getters.isLoggedOut) return

      let space = state.space

      try {
        await space.putAsync({
          subject: opts.subject,
          predicate: s(opts.predicate).slugify().s,
          object: opts.object
        })
      } catch (e) {
        commit('USER_ERR', e.message)
        throw e
      }
    },
    async recollect ({ state, rootState, commit, dispatch, getters, rootGetters }, opts) {
      if (getters.isLoggedOut) return

      let space = state.space

      try {
        let criteria = {
          subject: opts.subject || space.v('subject'),
          predicate: opts.predicate || space.v('predicate'),
          object: opts.object || space.v('object')
        }
        let recollection = await space.searchAsync(criteria)
        commit('RECOLLECT', { memories: recollection, ...criteria })
      } catch (e) {
        commit('USER_ERR', e.message)
        throw e
      }
    },
    async forget ({ state, rootState, commit, dispatch, getters, rootGetters }, opts) {
      if (getters.isLoggedOut) return

      let space = state.space

      try {
        await space.delAsync({
          subject: opts.memory.subject,
          predicate: s(opts.memory.predicate).slugify().s,
          object: opts.memory.object
        })

        commit('FORGOTTEN', opts.index)
      } catch (e) {
        commit('USER_ERR', e.message)
        throw e
      }
    }
  },
  modules: {}
}

export default module
